import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../lib/api'
import { onSocketEvent } from './useWebSocket'

export interface Notification {
  id: string
  organizationId?: string
  agentId?: string
  type: string
  title: string
  body?: string
  data?: Record<string, any>
  priority: 'low' | 'normal' | 'high' | 'urgent'
  readAt?: string | null
  createdAt: string
}

interface NotificationsState {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null
}

export function useNotifications() {
  const [state, setState] = useState<NotificationsState>({
    notifications: [],
    unreadCount: 0,
    loading: true,
    error: null
  })

  // Track seen IDs to prevent duplicates from WebSocket reconnects
  const seenIds = useRef<Set<string>>(new Set())

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications?limit=30')
      if (response.data.success) {
        const notifications: Notification[] = response.data.notifications
        // Rebuild seen IDs from fetched data
        seenIds.current = new Set(notifications.map(n => n.id))
        setState(prev => ({
          ...prev,
          notifications,
          unreadCount: response.data.unread,
          loading: false,
          error: null
        }))
        console.log('[Notifications] Loaded:', notifications.length, 'unread:', response.data.unread)
      }
    } catch (error: any) {
      const status = error?.response?.status
      console.error('[Notifications] Fetch failed:', { status, message: error?.message })
      setState(prev => ({
        ...prev,
        loading: false,
        error: status === 503 ? 'Service unavailable' : 'Failed to load notifications'
      }))
    }
  }, [])

  // Fetch on mount
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    const cleanup = onSocketEvent('notification', (notification: Notification) => {
      // Deduplicate — skip if already seen
      if (seenIds.current.has(notification.id)) {
        console.log('[Notifications] Duplicate skipped:', notification.id)
        return
      }
      seenIds.current.add(notification.id)
      console.log('[Notifications] Real-time notification received:', notification.type, notification.title)
      setState(prev => ({
        ...prev,
        notifications: [notification, ...prev.notifications].slice(0, 50),
        unreadCount: prev.unreadCount + 1
      }))
    })

    return cleanup
  }, [])

  const markAsRead = useCallback(async (notificationId: string) => {
    setState(prev => {
      const target = prev.notifications.find(n => n.id === notificationId)
      const wasUnread = target && !target.readAt
      return {
        ...prev,
        notifications: prev.notifications.map(n =>
          n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
        ),
        unreadCount: wasUnread ? Math.max(0, prev.unreadCount - 1) : prev.unreadCount
      }
    })

    try {
      await api.put(`/api/notifications/${notificationId}/read`)
      console.log('[Notifications] Marked as read:', notificationId)
    } catch (error) {
      console.error('[Notifications] Failed to mark as read:', error)
      fetchNotifications()
    }
  }, [fetchNotifications])

  const markAllAsRead = useCallback(async () => {
    // Snapshot current state for rollback
    const snapshot = { ...state }
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })),
      unreadCount: 0
    }))

    try {
      await api.put('/api/notifications/read-all')
      console.log('[Notifications] Marked all as read')
    } catch (error) {
      console.error('[Notifications] Failed to mark all as read:', error)
      setState(snapshot)
    }
  }, [state])

  // Count unread notifications by sidebar section
  const getUnreadBySection = useCallback((): Record<string, number> => {
    const counts: Record<string, number> = {}
    const typeToSection: Record<string, string> = {
      new_chat: '/chats',
      chat_assigned: '/chats',
      chat_waiting: '/chats',
      chat_completed: '/conversations',
      call_incoming: '/calls',
      call_completed: '/calls',
      call_missed: '/calls',
      sms_incoming: '/sms',
      booking_made: '/bookings',
      lead_created: '/bookings',
      team_message: '/messages',
    }

    for (const n of state.notifications) {
      if (n.readAt) continue
      const section = typeToSection[n.type]
      if (section) {
        counts[section] = (counts[section] || 0) + 1
      }
    }
    return counts
  }, [state.notifications])

  // Mark all notifications for a section as read (when user navigates to that section)
  const markSectionAsRead = useCallback(async (sectionPath: string) => {
    const sectionToTypes: Record<string, string[]> = {
      '/chats': ['new_chat', 'chat_assigned', 'chat_waiting'],
      '/conversations': ['chat_completed'],
      '/calls': ['call_incoming', 'call_completed', 'call_missed'],
      '/sms': ['sms_incoming'],
      '/bookings': ['booking_made', 'lead_created'],
      '/messages': ['team_message'],
    }

    const types = sectionToTypes[sectionPath]
    if (!types) return

    const unreadIds = state.notifications
      .filter(n => !n.readAt && types.includes(n.type))
      .map(n => n.id)

    if (unreadIds.length === 0) return

    // Optimistic update
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n =>
        unreadIds.includes(n.id) ? { ...n, readAt: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, prev.unreadCount - unreadIds.length)
    }))

    // Mark each as read on the server
    for (const id of unreadIds) {
      try {
        await api.put(`/api/notifications/${id}/read`)
      } catch {
        // Non-fatal
      }
    }
  }, [state.notifications])

  return {
    ...state,
    markAsRead,
    markAllAsRead,
    markSectionAsRead,
    getUnreadBySection,
    refresh: fetchNotifications
  }
}
